/**
 * Managed AI customer page: funding-source chooser and credit balance view.
 *
 * Rules:
 * - MS1-1: Default-off build, clean degradation when flag is off.
 * - MS1-3: Provider-neutral wording. No vendor names hardcoded; display names from API.
 * - MS1-6: Funding source seam (CUSTOMER_PROVIDER / AUTEUR_MANAGED). No fallback implied.
 * - MS1-7: Integer minor units for all money amounts through formatMoney; never float math.
 * - Plain-language copy, newbie-friendly; no jargon like ledger or reservation.
 * - No key material requested, accepted, or rendered.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AllowanceHistoryEntry,
  BalanceEntry,
  FundingSource,
  ManagedAiAllowance,
  ManagedAiBalance,
  ManagedAiFundingStatus,
  fetchManagedAllowance,
  fetchManagedBalance,
  fetchManagedFunding,
  updateFundingPreference,
} from '../../api/managedAi';
import { formatMoney } from '../../lib/money';
import { useSession } from '../../session';
import { pageStyles } from './ManagedAiPage.css';

function activityLabel(type: string): string {
  const normalized = type.trim().toLowerCase();
  switch (normalized) {
    case 'credit':
      return 'Credit added';
    case 'debit':
      return 'Usage';
    case 'hold':
      return 'Held for activity';
    case 'release':
      return 'Unused hold returned';
    case 'refund':
      return 'Refund';
    default:
      return type;
  }
}

function allowanceActivityLabel(kind: string): string {
  switch (kind) {
    case 'allowance_grant':
      return 'Monthly plan credits added';
    case 'allowance_lapse':
      return 'Unused plan credits expired';
    case 'credit_pack':
      return 'Credit pack added';
    case 'usage':
      return 'Usage';
    case 'usage_refund':
      return 'Usage refund';
    case 'refund':
      return 'Refund';
    case 'grant':
      return 'Credits added';
    default:
      return 'Adjustment';
  }
}

export function ManagedAiPage() {
  const { session } = useSession();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [fundingStatus, setFundingStatus] = useState<ManagedAiFundingStatus | null>(null);
  const [balance, setBalance] = useState<ManagedAiBalance | null>(null);
  const [balanceUnavailable, setBalanceUnavailable] = useState(false);
  const [allowance, setAllowance] = useState<ManagedAiAllowance | null>(null);

  const [selectedSource, setSelectedSource] = useState<FundingSource>('CUSTOMER_PROVIDER');
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setSaveFeedback(null);

    let fundingData: ManagedAiFundingStatus | null = null;
    try {
      fundingData = await fetchManagedFunding();
      setFundingStatus(fundingData);
      // When managed is unavailable, keep CUSTOMER_PROVIDER selected
      if (!fundingData.available) {
        setSelectedSource('CUSTOMER_PROVIDER');
      } else {
        setSelectedSource(fundingData.funding_source);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Unable to load funding settings.');
    }

    try {
      // Read-only plan allowance; any failure simply hides the section.
      const allowanceData = await fetchManagedAllowance();
      setAllowance(allowanceData && allowanceData.enabled ? allowanceData : null);
    } catch {
      setAllowance(null);
    }

    try {
      const balanceData = await fetchManagedBalance();
      setBalance(balanceData);
      setBalanceUnavailable(false);
    } catch {
      setBalanceUnavailable(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) {
      void loadData();
    }
  }, [session, loadData]);

  const handleSelectSource = (source: FundingSource) => {
    // If managed is unavailable, refuse selection and stay on CUSTOMER_PROVIDER
    if (source === 'AUTEUR_MANAGED' && (!fundingStatus || !fundingStatus.available)) {
      setSelectedSource('CUSTOMER_PROVIDER');
      return;
    }
    setSelectedSource(source);
    setSaveFeedback(null);
  };

  const handleSave = async () => {
    if (!fundingStatus) return;

    // Guard: refuse saving AUTEUR_MANAGED when unavailable
    if (selectedSource === 'AUTEUR_MANAGED' && !fundingStatus.available) {
      setSaveFeedback({
        kind: 'error',
        message: 'Auteur-managed credits are currently unavailable for this account.',
      });
      setSelectedSource('CUSTOMER_PROVIDER');
      return;
    }

    setSaving(true);
    setSaveFeedback(null);
    try {
      const updated = await updateFundingPreference(selectedSource);
      setFundingStatus(updated);
      setSelectedSource(updated.funding_source);
      setSaveFeedback({
        kind: 'success',
        message: 'Funding preference saved successfully.',
      });
    } catch (err) {
      setSaveFeedback({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to update funding preference.',
      });
      // On failure, re-sync with current server availability
      if (err instanceof Error && err.message.includes('unavailable')) {
        setSelectedSource('CUSTOMER_PROVIDER');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section style={pageStyles.container}>
        <p role="status">Loading AI funding settings…</p>
      </section>
    );
  }

  if (loadError) {
    return (
      <section style={pageStyles.container}>
        <div style={pageStyles.noticeBox} role="alert">
          <p>{loadError}</p>
          <button type="button" onClick={() => void loadData()} style={pageStyles.saveButton}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  const managedAvailable = Boolean(fundingStatus?.available);
  const refusalMessage =
    fundingStatus?.refusal_reason?.requirement ||
    fundingStatus?.refusal_reason?.missing ||
    (fundingStatus?.refusal_code ? 'Managed credits are not available for this account.' : '');

  const availableMoney = balance ? formatMoney(balance.available, balance.currency) : null;
  const inUseMoney = balance ? formatMoney(balance.held, balance.currency) : null;

  return (
    <section style={pageStyles.container} aria-label="AI Funding & Credits">
      <header style={pageStyles.header}>
        <p className="eyebrow">AI Usage</p>
        <h1>AI Funding & Credits</h1>
        <p className="page-note">
          Choose how your AI usage is funded and monitor your Auteur credits.
        </p>
      </header>

      {/* Part 1: Funding-source chooser */}
      <div style={pageStyles.section}>
        <h2 style={pageStyles.sectionTitle}>Funding source</h2>
        <p style={pageStyles.sectionDescription}>
          Select whether AI requests use your own API keys or credits provided by Auteur.
        </p>

        {!managedAvailable && refusalMessage ? (
          <div style={pageStyles.noticeBox} role="alert" data-state="Unavailable">
            <strong>Auteur-managed credits unavailable:</strong> {refusalMessage}
            <br />
            <span>Your requests will continue using your own provider keys.</span>
          </div>
        ) : null}

        {/* Option 1: Customer Provider Keys */}
        <div
          style={{
            ...pageStyles.optionCard,
            ...(selectedSource === 'CUSTOMER_PROVIDER' ? pageStyles.optionCardSelected : {}),
          }}
          onClick={() => handleSelectSource('CUSTOMER_PROVIDER')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              handleSelectSource('CUSTOMER_PROVIDER');
            }
          }}
        >
          <input
            type="radio"
            name="funding_source"
            id="funding-customer-keys"
            value="CUSTOMER_PROVIDER"
            checked={selectedSource === 'CUSTOMER_PROVIDER'}
            onChange={() => handleSelectSource('CUSTOMER_PROVIDER')}
            style={pageStyles.radio}
          />
          <div style={pageStyles.optionContent}>
            <label htmlFor="funding-customer-keys" style={pageStyles.optionTitle}>
              My own provider keys
            </label>
            <p style={pageStyles.optionDescription}>
              Use your own API keys configured in Provider Keys. You pay your providers directly.
            </p>
          </div>
        </div>

        {/* Option 2: Auteur-managed credits */}
        <div
          style={{
            ...pageStyles.optionCard,
            ...(selectedSource === 'AUTEUR_MANAGED' ? pageStyles.optionCardSelected : {}),
            ...(!managedAvailable ? pageStyles.optionCardDisabled : {}),
          }}
          onClick={() => {
            if (managedAvailable) {
              handleSelectSource('AUTEUR_MANAGED');
            }
          }}
          role="button"
          tabIndex={managedAvailable ? 0 : -1}
          aria-disabled={!managedAvailable}
          onKeyDown={(e) => {
            if (managedAvailable && (e.key === ' ' || e.key === 'Enter')) {
              e.preventDefault();
              handleSelectSource('AUTEUR_MANAGED');
            }
          }}
        >
          <input
            type="radio"
            name="funding_source"
            id="funding-auteur-managed"
            value="AUTEUR_MANAGED"
            checked={selectedSource === 'AUTEUR_MANAGED'}
            disabled={!managedAvailable}
            onChange={() => {
              if (managedAvailable) {
                handleSelectSource('AUTEUR_MANAGED');
              }
            }}
            style={pageStyles.radio}
          />
          <div style={pageStyles.optionContent}>
            <label htmlFor="funding-auteur-managed" style={pageStyles.optionTitle}>
              Auteur-managed credits
            </label>
            <p style={pageStyles.optionDescription}>
              Use your Auteur credits. No separate provider accounts or personal keys required.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || (!managedAvailable && selectedSource === 'AUTEUR_MANAGED')}
          style={pageStyles.saveButton}
        >
          {saving ? 'Saving preference…' : 'Save preference'}
        </button>

        {saveFeedback?.kind === 'success' ? (
          <p style={pageStyles.feedbackSuccess} role="status">
            {saveFeedback.message}
          </p>
        ) : null}
        {saveFeedback?.kind === 'error' ? (
          <p style={pageStyles.feedbackError} role="alert">
            {saveFeedback.message}
          </p>
        ) : null}
      </div>

      {/* Part 2: Balance view */}
      <div style={pageStyles.section}>
        <h2 style={pageStyles.sectionTitle}>Credits balance</h2>
        <p style={pageStyles.sectionDescription}>
          Your current available Auteur credits and active usage.
        </p>

        {balanceUnavailable ? (
          <div style={pageStyles.noticeBox} role="status" data-state="Unavailable">
            Credit balance details are not currently available for this account.
          </div>
        ) : (
          <>
            <div style={pageStyles.balanceGrid}>
              <div style={pageStyles.metricCard}>
                <div style={pageStyles.metricLabel}>Available credits</div>
                <div style={pageStyles.metricValue}>{availableMoney ?? '$0.00'}</div>
              </div>
              <div style={pageStyles.metricCard}>
                <div style={pageStyles.metricLabel}>In use</div>
                <div style={pageStyles.metricValue}>{inUseMoney ?? '$0.00'}</div>
              </div>
              <div style={pageStyles.metricCard}>
                <div style={pageStyles.metricLabel}>Currency</div>
                <div style={pageStyles.metricValue}>{balance?.currency || 'USD'}</div>
              </div>
            </div>

            {balance?.last_updated ? (
              <p style={{ color: 'var(--muted, #a99db5)', fontSize: '0.85rem' }}>
                Last updated: {balance.last_updated}
              </p>
            ) : null}

            <h3 style={{ ...pageStyles.sectionTitle, fontSize: '1.1rem', marginTop: '1.5rem' }}>
              Recent activity
            </h3>
            {balance && balance.recent_entries && balance.recent_entries.length > 0 ? (
              <table style={pageStyles.table}>
                <thead>
                  <tr>
                    <th style={pageStyles.th}>Activity</th>
                    <th style={pageStyles.th}>Amount</th>
                    <th style={pageStyles.th}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {balance.recent_entries.map((entry: BalanceEntry, idx: number) => {
                    const formatted = formatMoney(Math.abs(entry.amount), balance.currency);
                    const sign = entry.amount < 0 ? '-' : '+';
                    return (
                      <tr key={idx}>
                        <td style={pageStyles.td}>{activityLabel(entry.type)}</td>
                        <td style={pageStyles.td}>
                          {formatted ? `${sign}${formatted}` : '—'}
                        </td>
                        <td style={pageStyles.td}>{entry.time || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p style={pageStyles.emptyActivity}>No recent credit activity.</p>
            )}
          </>
        )}
      </div>

      {/* Part 3: Plan allowance vs credit packs (read-only) */}
      {allowance ? (
        <div style={pageStyles.section} aria-label="Plan credits">
          <h2 style={pageStyles.sectionTitle}>Plan credits and credit packs</h2>
          <p style={pageStyles.sectionDescription}>
            Your plan adds monthly credits that are used first and expire at the end of each
            billing period. Credit packs you buy never expire.
          </p>
          <div style={pageStyles.balanceGrid}>
            <div style={pageStyles.metricCard}>
              <div style={pageStyles.metricLabel}>Monthly plan credits left</div>
              <div style={pageStyles.metricValue}>
                {formatMoney(allowance.allowance, allowance.currency || 'USD') ?? '$0.00'}
              </div>
            </div>
            <div style={pageStyles.metricCard}>
              <div style={pageStyles.metricLabel}>Credit packs</div>
              <div style={pageStyles.metricValue}>
                {formatMoney(allowance.pack, allowance.currency || 'USD') ?? '$0.00'}
              </div>
            </div>
            <div style={pageStyles.metricCard}>
              <div style={pageStyles.metricLabel}>Plan credits renew</div>
              <div style={pageStyles.metricValue}>{allowance.renewal_at || '—'}</div>
            </div>
          </div>
          {allowance.available <= 0 ? (
            <div style={pageStyles.noticeBox} role="status" data-state="Exhausted">
              Your credits are used up. {allowance.overage_action}
            </div>
          ) : null}
          <p style={{ color: 'var(--muted, #a99db5)', fontSize: '0.85rem' }}>
            Automatic top-up is {allowance.auto_top_up ? 'on' : 'off'}.
          </p>
          <h3 style={{ ...pageStyles.sectionTitle, fontSize: '1.1rem', marginTop: '1.5rem' }}>
            Credit history
          </h3>
          {allowance.history.length > 0 ? (
            <table style={pageStyles.table}>
              <thead>
                <tr>
                  <th style={pageStyles.th}>Activity</th>
                  <th style={pageStyles.th}>Amount</th>
                  <th style={pageStyles.th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {allowance.history.map((entry: AllowanceHistoryEntry, idx: number) => {
                  const formatted = formatMoney(Math.abs(entry.amount), allowance.currency || 'USD');
                  const sign = entry.amount < 0 ? '-' : '+';
                  return (
                    <tr key={idx}>
                      <td style={pageStyles.td}>{allowanceActivityLabel(entry.kind)}</td>
                      <td style={pageStyles.td}>{formatted ? `${sign}${formatted}` : '—'}</td>
                      <td style={pageStyles.td}>{entry.time || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p style={pageStyles.emptyActivity}>No plan credit activity yet.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

export default ManagedAiPage;
